// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import translate from "@insite/client-framework/Translate";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { resolveColor } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import { blogTileImageStyles, blogTileWrapperStyles } from "@store/Themes/common/mixins/blog";
import { linkAltStyles } from "@store/Themes/common/mixins/link";
import { blogProps } from "@store/Themes/common/props/blog";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useCurrentLanguage } from "@toolkit/Framework/Store/Context/ContextHooks";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import truncate from "lodash/truncate";
import React, { FC, HTMLAttributes } from "react";
import { useMemo } from "react";
import { css } from "styled-components";

const tileContentPadding = 18;
export interface BlogTilePresentationProps extends ExpandStyles<BlogTileStyles> {
    contentBackgroundColor?: string;
}

export interface BlogTileProps extends BlogTilePresentationProps, HTMLAttributes<HTMLElement> {
    category: string | undefined;
    imagePath: string;
    linkHref: string | undefined;
    publishDate: Date;
    title: string;
}

export interface BlogTileStyles {
    wrapper?: InjectableCss;
    tileClickable?: ClickablePresentationProps;
    surtitle?: TypographyPresentationProps;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    content?: InjectableCss;
    header?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    title?: TypographyPresentationProps;
    footer?: InjectableCss;
    publishDate?: InjectableCss;
}

export const blogTileStyles: BlogTileStyles = {
    wrapper: {
        css: css`
            ${blogTileWrapperStyles()}
        `,
    },
    tileClickable: mergeProps<BlogTileStyles["tileClickable"]>(blogProps.tileClickable, {
        css: css`
            &:hover {
                ~ div {
                    img {
                        scale: none;

                        ${breakpoint("md")} {
                            scale: 1.1;
                        }
                    }
                }
            }
        `,
    }),

    imageWrapper: blogProps.imageWrapper,
    image: {
        css: css`
            ${x => x.theme.lazyImage.defaultProps?.css}
            display: flex;

            img {
                ${blogTileImageStyles()}
            }
        `,
    },
    content: {
        css: css`
            flex-grow: 1;
            padding: ${pxToRem(tileContentPadding, 0)};
        `,
    },
    surtitle: blogProps.surtitle,
    titleClickable: {
        css: css`
            ${linkAltStyles()}
            ${spacingStyles(pxToRem(6), null)}
        `,
    },
    title: {
        variant: "h4",
        forwardAs: "h3",
    },
    footer: {
        css: css`
            ${spacingStyles(pxToRem(6), null)}
        `,
    },
    publishDate: blogProps.publishDate,
};

const BlogTile: FC<BlogTileProps> = ({
    extendedStyles,
    overridedStyles,
    category: baseCategory,
    contentBackgroundColor,
    imagePath,
    linkHref,
    publishDate,
    title: baseTitle,
    ...htmlAttributes
}) => {
    const styles = useExpandProps(blogTileStyles, extendedStyles, overridedStyles);
    const language = useCurrentLanguage();

    const contentStyles = useMemo(() => {
        return mergeProps<BlogTileStyles["content"]>(styles.content, {
            css: css`
                ${({ theme }) => {
                    if (contentBackgroundColor) {
                        return css`
                            padding: ${pxToRem(tileContentPadding)};
                            background-color: ${resolveColor(contentBackgroundColor, theme)};
                        `;
                    }
                }}
            `,
        });
    }, [styles.content, contentBackgroundColor]);

    if (!linkHref) {
        return null;
    }

    const title = truncate(baseTitle, { length: 160 });
    const category = truncate(baseCategory, { length: 120 });

    const publishDateDisplay = getLocalizedDateTime({
        dateTime: new Date(publishDate),
        language,
        options: constants.blog.getLocalizedDateTimeOptions,
    });

    return (
        <StyledWrapper {...styles.wrapper} as="article" {...htmlAttributes}>
            <Clickable {...styles.tileClickable} href={linkHref} tabIndex={-1} aria-hidden="true">
                {translate("{0} {1} Publish {2}", category, title, publishDateDisplay)}
            </Clickable>
            <StyledWrapper {...styles.imageWrapper} aria-hidden="true">
                <LazyImage {...styles.image} src={imagePath} />
            </StyledWrapper>
            <StyledWrapper {...contentStyles} as="section">
                <StyledWrapper {...styles.header} as="header">
                    {category ? (
                        <Typography {...styles.surtitle} aria-hidden="true">
                            {category}
                        </Typography>
                    ) : null}
                    <Clickable
                        {...styles.titleClickable}
                        href={linkHref}
                        aria-label={translate("{0} {1} Publish {2}", category, title, publishDateDisplay)}
                    >
                        <Typography {...styles.title}>{title}</Typography>
                    </Clickable>
                </StyledWrapper>
                <StyledWrapper {...styles.footer} as="footer" aria-hidden="true">
                    <StyledWrapper {...styles.publishDate} as="time" dateTime={publishDate.toString()}>
                        {publishDateDisplay}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default BlogTile;
